import { useDocumentSearch } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentSearch'
import {
  appendAgenticStreamEventToConversation,
  markAgenticConversationTurnError,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/agenticConversation'
import type {
  AgenticSearchSseEvent,
  DocumentSearchResult,
  SearchEngine,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import type {
  SearchHistoryItem,
  SearchStateUpdate,
} from 'features/knowledgeDocumentViewer/types'
import { useCallback, useEffect } from 'react'

interface UseAgenticDocumentSearchArgs {
  enabled: boolean
  siteId: string
  tabId?: string
  searchQuery: string
  limit?: number
  agenticSearch?: SearchHistoryItem['searchState']['agenticSearch']
  engine?: Exclude<SearchEngine, 'unified'>
}

interface UseAgenticDocumentSearchResult {
  rawDocumentSearchData: DocumentSearchResult | undefined
  isLoadingSearch: boolean
  searchLatencyMs: number | undefined
  refreshDocumentSearch: () => void
}

export function useAgenticDocumentSearch({
  enabled,
  siteId,
  tabId,
  searchQuery,
  limit = 200,
  agenticSearch,
  engine = 'agentic',
}: UseAgenticDocumentSearchArgs): UseAgenticDocumentSearchResult {
  const { onSearchStateChange: onSearchStateChangeForActiveTab } =
    useSearchActions()
  const onSearchStateChange = useCallback(
    (updates: SearchStateUpdate) => {
      if (tabId) {
        onSearchStateChangeForActiveTab(updates, { tabId })
        return
      }
      onSearchStateChangeForActiveTab(updates)
    },
    [onSearchStateChangeForActiveTab, tabId]
  )
  const agenticConversation =
    agenticSearch?.siteId === siteId ? agenticSearch.conversation : undefined
  const agenticConversationId = agenticConversation?.id
  const agenticActiveTurnId = agenticConversation?.activeTurnId

  const handleAgenticStreamEvent = useCallback(
    (streamEvent: AgenticSearchSseEvent) => {
      if (streamEvent.site_id !== siteId) return
      onSearchStateChange((currentSearchState) => {
        const currentAgenticSearch = currentSearchState.agenticSearch
        const currentConversation =
          currentAgenticSearch?.siteId === siteId
            ? currentAgenticSearch.conversation
            : undefined
        if (!currentConversation) return {}
        const nextConversation = appendAgenticStreamEventToConversation({
          conversation: currentConversation,
          streamEvent,
          siteId,
        })
        if (nextConversation === currentConversation) return {}
        return {
          agenticSearch: {
            siteId,
            conversation: nextConversation,
          },
        }
      })
    },
    [onSearchStateChange, siteId]
  )

  const {
    data: rawDocumentSearchData,
    error: searchError,
    isLoading: isLoadingSearch,
    mutate: refreshRawDocumentSearch,
    searchLatencyMs,
  } = useDocumentSearch(
    {
      query: searchQuery,
      site_id: siteId,
      limit,
      ...(agenticConversationId
        ? { conversation_id: agenticConversationId }
        : {}),
      ...(agenticActiveTurnId ? { request_id: agenticActiveTurnId } : {}),
    },
    undefined,
    engine,
    {
      enabled,
      onEvent: handleAgenticStreamEvent,
    }
  )

  useEffect(() => {
    if (!enabled || !searchError || !agenticActiveTurnId) return
    onSearchStateChange((currentSearchState) => {
      const currentAgenticSearch = currentSearchState.agenticSearch
      const currentConversation =
        currentAgenticSearch?.siteId === siteId
          ? currentAgenticSearch.conversation
          : undefined
      if (!currentConversation) return {}
      if (currentConversation.activeTurnId !== agenticActiveTurnId) return {}
      const nextConversation = markAgenticConversationTurnError({
        conversation: currentConversation,
        turnId: agenticActiveTurnId,
        siteId,
      })
      if (nextConversation === currentConversation) return {}
      return {
        agenticSearch: {
          siteId,
          conversation: nextConversation,
        },
      }
    })
  }, [agenticActiveTurnId, enabled, onSearchStateChange, searchError, siteId])

  return {
    rawDocumentSearchData,
    isLoadingSearch,
    searchLatencyMs,
    refreshDocumentSearch: () => {
      if (!enabled || !searchQuery.trim()) return
      void refreshRawDocumentSearch()
    },
  }
}
